import { useEffect, useState, type MouseEvent, type ReactNode } from "react";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import Link from "@mui/material/Link";
import envConfig from "../config/env";
import { useGuardedAsyncCallback } from "../hooks/useGuardedAsyncCallback";
import { getWorkflow } from "../services/regulationsService";
import {
  buildRegulationDetailHref,
  NAV_SOURCE_OMIT_DOMAIN,
} from "./externalNavigation";
import {
  MANUAL_ASSESSMENT_SECTION_ID,
  SECTION_QUERY_PARAM,
  WHOLE_WORKFLOW_QUERY_PARAM,
} from "./wholeWorkflowIframe";

/** PAT / Associate Process base URL (VITE_patUrl preferred, else API base). */
export function getManualAssessmentProcessPatBaseUrl(): string {
  return (envConfig.associateUrl?.trim() || envConfig.planTrackBaseUrl).replace(
    /\/$/,
    "",
  );
}

export function getManualAssessmentProcessPatHref(
  processAssociationId: string,
): string | null {
  const id = processAssociationId?.trim();
  if (!id) return null;
  return `${getManualAssessmentProcessPatBaseUrl()}/process/${id}`;
}

function isPlaceholderGridValue(value: string): boolean {
  const trimmed = value.trim();
  return !trimmed || trimmed === "—" || trimmed === "-";
}

/**
 * Assessment Center Process Name → new window on Regulation Details, Manual
 * Impact Assessment tab, with the same PAT process URI in the split pane.
 */
export function buildManualAssessmentProcessDetailsHref(
  regulationId: string,
  version: string,
  processAssociationId: string,
): string | null {
  const id = regulationId.trim();
  const associationId = processAssociationId.trim();
  if (isPlaceholderGridValue(id) || !associationId) return null;

  const versionTrimmed = version.trim();
  return buildRegulationDetailHref(id, {
    ...(isPlaceholderGridValue(versionTrimmed)
      ? {}
      : { version: versionTrimmed }),
    source: NAV_SOURCE_OMIT_DOMAIN.ASSESSMENT_CENTER,
    extraQuery: {
      [WHOLE_WORKFLOW_QUERY_PARAM]: associationId,
      [SECTION_QUERY_PARAM]: MANUAL_ASSESSMENT_SECTION_ID,
    },
    hash: MANUAL_ASSESSMENT_SECTION_ID,
  });
}

/** Whether the user may open the PAT process page for this workflow row. */
export function isManualAssessmentProcessNameNavigable(
  status: string,
  canManageManualAssessment: boolean,
): boolean {
  const isCompleted = (status || "").toLowerCase() === "completed";
  return canManageManualAssessment || isCompleted;
}

/** @deprecated Prefer {@link isManualAssessmentProcessNameNavigable}. */
export function canShowManualAssessmentProcessNameLink(
  processAssociationId: string,
  status: string,
  canManageManualAssessment: boolean,
): boolean {
  if (!processAssociationId?.trim()) return false;
  return isManualAssessmentProcessNameNavigable(
    status,
    canManageManualAssessment,
  );
}

const processAssociationIdByWorkflowUuid = new Map<string, string>();
const processAssociationIdInflight = new Map<string, Promise<string>>();

export function __resetProcessAssociationIdCacheForTests(): void {
  processAssociationIdByWorkflowUuid.clear();
  processAssociationIdInflight.clear();
}

export async function fetchProcessAssociationIdForWorkflow(
  workflowUuid: string,
): Promise<string> {
  const key = workflowUuid.trim();
  if (!key) return "";

  if (processAssociationIdByWorkflowUuid.has(key)) {
    return processAssociationIdByWorkflowUuid.get(key) ?? "";
  }

  const inflight = processAssociationIdInflight.get(key);
  if (inflight) return inflight;

  const request = (async () => {
    let resolved = "";
    try {
      const res = await getWorkflow(key);
      const detail = res.data as Record<string, unknown> | null;
      resolved = String(
        detail?.processAssociationId ?? detail?.process_association_id ?? "",
      ).trim();
    } catch {
      resolved = "";
    }
    processAssociationIdByWorkflowUuid.set(key, resolved);
    return resolved;
  })();

  processAssociationIdInflight.set(key, request);
  try {
    return await request;
  } finally {
    processAssociationIdInflight.delete(key);
  }
}

export type ManualAssessmentProcessDetailsNav = Readonly<{
  regulationId: string;
  version: string;
}>;

export type ManualAssessmentProcessNameCellProps = Readonly<{
  processName: string;
  workflowUuid: string;
  processAssociationId?: string;
  status: string;
  canManageManualAssessment: boolean;
  /** When set, primary click opens the PAT process in-page instead of a new tab. */
  onOpenPatProcess?: (url: string) => void;
  /** Fires on click before the PAT URL is opened (for immediate row loading UI). */
  onOpenStart?: () => void;
  /** Show a spinner next to the process name while the half-page pane is opening. */
  isOpening?: boolean;
  /**
   * Assessment Center: open Regulation Details in a new window, jump to
   * Manual Impact Assessment, and half-page the same PAT process URL.
   */
  detailsWholeWorkflow?: ManualAssessmentProcessDetailsNav;
}>;

/** Table process-name links keep fixed blue; not branding primaryColor */
const TABLE_PROCESS_LINK_COLOR = "#2563eb";

const PROCESS_NAME_LINK_SX = {
  cursor: "pointer",
  verticalAlign: "middle",
  background: "none",
  border: "none",
  padding: 0,
  font: "inherit",
  textAlign: "left",
  color: `${TABLE_PROCESS_LINK_COLOR} !important`,
  textDecorationColor: TABLE_PROCESS_LINK_COLOR,
  "&:hover": {
    color: `${TABLE_PROCESS_LINK_COLOR} !important`,
    textDecorationColor: TABLE_PROCESS_LINK_COLOR,
  },
} as const;

function ProcessNameOpeningWrap({
  isOpening,
  children,
}: {
  isOpening: boolean;
  children: ReactNode;
}) {
  return (
    <Box
      className="manual-ia-process-name"
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        gap: 0.75,
        width: "100%",
        minWidth: 0,
        textAlign: "left",
      }}
    >
      {isOpening ? (
        <CircularProgress
          size={14}
          aria-label="Opening process"
          data-testid="manual-ia-process-name-opening"
        />
      ) : null}
      {children}
    </Box>
  );
}

function openPatProcessInNewTab(url: string): void {
  const popup = window.open("about:blank", "_blank");
  if (popup) {
    popup.opener = null;
    popup.location.replace(url);
    return;
  }
  window.open(url, "_blank", "noopener,noreferrer");
}

function isPrimaryUnmodifiedClick(event: MouseEvent<HTMLElement>): boolean {
  return (
    event.button === 0 &&
    !event.metaKey &&
    !event.ctrlKey &&
    !event.shiftKey &&
    !event.altKey
  );
}

export function ManualAssessmentProcessNameCell({
  processName,
  workflowUuid,
  processAssociationId: initialProcessAssociationId = "",
  status,
  canManageManualAssessment,
  onOpenPatProcess,
  onOpenStart,
  isOpening = false,
  detailsWholeWorkflow,
}: ManualAssessmentProcessNameCellProps) {
  const label = processName?.trim() ? processName : "—";
  const [resolvedAssociationId, setResolvedAssociationId] = useState(
    initialProcessAssociationId.trim(),
  );

  const navigable = isManualAssessmentProcessNameNavigable(
    status,
    canManageManualAssessment,
  );

  useEffect(() => {
    const fromProps = initialProcessAssociationId.trim();
    if (fromProps) {
      setResolvedAssociationId(fromProps);
    }
  }, [initialProcessAssociationId]);

  const detailsHrefForAssociation = (associationId: string): string | null => {
    if (!detailsWholeWorkflow || onOpenPatProcess) return null;
    return buildManualAssessmentProcessDetailsHref(
      detailsWholeWorkflow.regulationId,
      detailsWholeWorkflow.version,
      associationId,
    );
  };

  const handleOpenPat = useGuardedAsyncCallback(async () => {
    const existing = resolvedAssociationId.trim();
    if (existing) {
      const detailsHref = detailsHrefForAssociation(existing);
      if (detailsHref) {
        openPatProcessInNewTab(detailsHref);
        return;
      }
      const href = getManualAssessmentProcessPatHref(existing);
      if (!href) return;
      if (onOpenPatProcess) {
        onOpenPatProcess(href);
        return;
      }
      openPatProcessInNewTab(href);
      return;
    }

    if (onOpenPatProcess) {
      const id = await fetchProcessAssociationIdForWorkflow(workflowUuid);
      if (id) setResolvedAssociationId(id);
      const href = getManualAssessmentProcessPatHref(id);
      if (href) onOpenPatProcess(href);
      return;
    }

    const popup = window.open("about:blank", "_blank");
    if (popup) popup.opener = null;

    const id = await fetchProcessAssociationIdForWorkflow(workflowUuid);
    if (id) setResolvedAssociationId(id);
    const href =
      detailsHrefForAssociation(id) ?? getManualAssessmentProcessPatHref(id);
    if (href) {
      if (popup) {
        popup.location.replace(href);
        return;
      }
      window.open(href, "_blank", "noopener,noreferrer");
      return;
    }
    popup?.close();
  });

  if (!navigable) {
    return <>{label}</>;
  }

  const patHref = getManualAssessmentProcessPatHref(resolvedAssociationId);
  const detailsHref = resolvedAssociationId
    ? detailsHrefForAssociation(resolvedAssociationId)
    : null;
  const href = detailsHref ?? patHref;
  if (href) {
    const openInPane = Boolean(onOpenPatProcess);
    return (
      <ProcessNameOpeningWrap isOpening={isOpening}>
        <Link
          href={href}
          target={openInPane ? undefined : "_blank"}
          rel="noopener noreferrer"
          underline="always"
          color="inherit"
          aria-busy={isOpening || undefined}
          sx={PROCESS_NAME_LINK_SX}
          onClick={(event) => {
            if (!onOpenPatProcess || !isPrimaryUnmodifiedClick(event)) return;
            event.preventDefault();
            event.stopPropagation();
            if (isOpening) return;
            onOpenStart?.();
            if (patHref) onOpenPatProcess(patHref);
          }}
        >
          {label}
        </Link>
      </ProcessNameOpeningWrap>
    );
  }

  const canResolve = Boolean(workflowUuid.trim());
  if (!canResolve) {
    return <>{label}</>;
  }

  const onPendingClick = (event: MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    if (isOpening) return;
    onOpenStart?.();
    void handleOpenPat();
  };

  return (
    <ProcessNameOpeningWrap isOpening={isOpening}>
      <Link
        component="button"
        type="button"
        underline="always"
        color="inherit"
        aria-busy={isOpening || undefined}
        onClick={onPendingClick}
        sx={PROCESS_NAME_LINK_SX}
      >
        {label}
      </Link>
    </ProcessNameOpeningWrap>
  );
}

export function renderManualAssessmentProcessNameCell(
  processName: string,
  workflowUuid: string,
  processAssociationId: string,
  status: string,
  canManageManualAssessment: boolean,
  onOpenPatProcess?: (url: string) => void,
  detailsWholeWorkflow?: ManualAssessmentProcessDetailsNav,
): ReactNode {
  return (
    <ManualAssessmentProcessNameCell
      processName={processName}
      workflowUuid={workflowUuid}
      processAssociationId={processAssociationId}
      status={status}
      canManageManualAssessment={canManageManualAssessment}
      onOpenPatProcess={onOpenPatProcess}
      detailsWholeWorkflow={detailsWholeWorkflow}
    />
  );
}
